<template>
  <div>
    <platform-modal
      v-model="showModal"
      :title="$t('organisationDashboard.saveConfiguration.modalTitle')"
      icon="save"
      primary
      :width="600"
      :saving="saving"
      @save="saveConfiguration"
      @close="$emit('close')"
    >
      <template #dangerActions>
        <div class="d-flex ml-auto">
          <platform-button
            warning
            icon="cog-refresh-outline"
            class="mr-2"
            @click="showResetConfirmation"
          >
            {{ $t('organisationDashboard.filters.resetToDefault') }}
          </platform-button>
        </div>
      </template>
      <div class="py-2">
        <p>{{ $t('organisationDashboard.saveConfiguration.modalMessage') }}</p>
        <div v-if="!showHistoricalWarning && rollingDays">
          <div class="d-flex align-center justify-center pb-2">
            <platform-icon
              name="filter"
              color="primary"
              :size="36"
            />
            <strong class="primary--text">
              {{ $t('organisationDashboard.saveConfiguration.dateFilters') }}: {{ $t('organisationDashboard.saveConfiguration.rollingDays', { count: rollingDays }) }}
            </strong>
          </div>
          <p>{{ $t('organisationDashboard.saveConfiguration.rollingDaysWarning') }}</p>
        </div>
        <div v-else-if="startDateOnly">
          <div class="d-flex align-center justify-center pb-2">
            <platform-icon
              name="filter"
              color="primary"
              :size="36"
            />
            <strong class="primary--text">
              {{ $t('organisationDashboard.saveConfiguration.dateFilters') }}: {{ $t('organisationDashboard.saveConfiguration.fromDay', { date: formattedStartDate }) }}
            </strong>
          </div>
          <p>{{ $t('organisationDashboard.saveConfiguration.fromDayWarning') }}</p>
        </div>
        <div v-else-if="endDateOnly">
          <div class="d-flex align-center justify-center pb-2">
            <platform-icon
              name="filter"
              color="primary"
              :size="36"
            />
            <strong class="primary--text">
              {{ $t('organisationDashboard.saveConfiguration.dateFilters') }}: {{ $t('organisationDashboard.saveConfiguration.untilDate', { date: formattedEndDate }) }}
            </strong>
          </div>
          <p>{{ $t('organisationDashboard.saveConfiguration.untilDateWarning') }}</p>
        </div>
        <div v-else-if="showHistoricalWarning">
          <div class="d-flex align-center justify-center pb-2">
            <platform-icon
              name="filter"
              color="primary"
              :size="36"
            />
            <strong class="primary--text">
              {{ $t('organisationDashboard.saveConfiguration.dateFilters') }}: {{ formattedStartDate }} - {{ formattedEndDate }}
            </strong>
          </div>
          <p>{{ $t('organisationDashboard.saveConfiguration.historicalDataWarning') }}</p>
        </div>
        <p class="warning--text">
          {{ $t('organisationDashboard.saveConfiguration.warningMessage') }}
        </p>
      </div>
    </platform-modal>
    <confirmation-modal
      v-if="resetConfirmationVisible"
      entity-type="settings"
      :image="false"
      :loading="saving"
      force-icon="cog-refresh-outline"
      :force-title="$t('organisationDashboard.saveConfiguration.resetConfirmationTitle')"
      :message="$t('organisationDashboard.saveConfiguration.resetConfirmationMessage')"
      @confirm="confirmResetToDefault"
      @cancel="resetConfirmationVisible = false"
    />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import dayjs from 'dayjs'
import API from '@/services/API'
import PlatformModal from '@/components/PlatformModal.vue'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import {
  DashboardConfiguration,
  DashboardDateRange,
  DashboardFilters,
  DEFAULT_DASHBOARD_CONFIGURATION,
  DashboardTableOptions,
  FilterState,
  TableOptionsState
} from '@betterboards/shared/types/OrganisationUser'
import PlatformIcon from '../shared/PlatformIcon.vue'

export default Vue.extend({
  name: 'SaveDashboardConfigurationModal',
  components: {
    PlatformModal,
    PlatformIcon,
    ConfirmationModal
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    filterState: {
      type: Object as PropType<FilterState>,
      required: true
    },
    tableOptions: {
      type: Object as PropType<TableOptionsState>,
      required: true
    }
  },
  data: () => ({
    saving: false,
    resetConfirmationVisible: false
  }),
  computed: {
    showModal: {
      get (): boolean {
        return this.value
      },
      set (val: boolean): void {
        this.$emit('input', val)
      }
    },
    showHistoricalWarning (): boolean {
      const today = dayjs().format('YYYY-MM-DD')
      const hasEndDate = !!this.filterState.endDate
      const endDateIsNotToday = hasEndDate && this.filterState.endDate !== today

      return endDateIsNotToday
    },
    formattedStartDate (): string {
      if (!this.filterState.startDate) {
        return ''
      }
      return dayjs(this.filterState.startDate).format('D MMM YYYY')
    },
    formattedEndDate (): string {
      if (!this.filterState.endDate) {
        return ''
      }
      return dayjs(this.filterState.endDate).format('D MMM YYYY')
    },
    rollingDays (): number | null {
      const today = dayjs().format('YYYY-MM-DD')
      const hasStartDate = !!this.filterState.startDate
      const hasEndDate = !!this.filterState.endDate
      const endDateIsToday = hasEndDate && this.filterState.endDate === today

      if (!hasStartDate || !hasEndDate || !endDateIsToday) {
        return null
      }

      const start = dayjs(this.filterState.startDate)
      const end = dayjs(this.filterState.endDate)

      return end.diff(start, 'day') + 1
    },
    startDateOnly (): boolean {
      return !!this.filterState.startDate && !this.filterState.endDate
    },
    endDateOnly (): boolean {
      return !this.filterState.startDate && !!this.filterState.endDate
    }
  },
  methods: {
    buildDashboardConfiguration (): DashboardConfiguration {
      const today = dayjs()
      const todayString = today.format('YYYY-MM-DD')
      const hasStartDate: boolean = !!this.filterState.startDate
      const hasEndDate: boolean = !!this.filterState.endDate
      const hasBothDates = hasStartDate && hasEndDate
      const endDateIsToday = hasEndDate && this.filterState.endDate === todayString

      // If both dates are set AND end date is today, save as relative (rolling window)
      // Otherwise, save as absolute (fixed period)
      let dateRange: DashboardDateRange | undefined
      if (hasBothDates && endDateIsToday) {
        dateRange = {
          type: 'relative',
          startDaysFromToday: dayjs(this.filterState.startDate).diff(today, 'day'),
          endDaysFromToday: dayjs(this.filterState.endDate).diff(today, 'day')
        }
      } else {
        dateRange = {
          type: 'absolute',
          startDate: this.filterState.startDate,
          endDate: this.filterState.endDate
        }
      }

      const filters: DashboardFilters = {
        selectedCompanyFilter: this.filterState.selectedCompanyFilter,
        selectedSectorFilter: this.filterState.selectedSectorFilter,
        selectedConsultantFilter: this.filterState.selectedConsultantFilter,
        selectedStatusFilter: this.filterState.selectedStatusFilter,
        dateRange,
        searchText: this.filterState.searchText
      }

      const tableOptions: DashboardTableOptions = {
        itemsPerPage: this.tableOptions.itemsPerPage,
        sortBy: this.tableOptions.sortBy,
        sortDesc: this.tableOptions.sortDesc
      }

      return {
        filters,
        tableOptions
      }
    },
    async saveConfiguration (): Promise<void> {
      this.saving = true

      try {
        const dashboardConfiguration = this.buildDashboardConfiguration()

        await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'saveDashboardConfiguration',
            dashboardConfiguration
          }
        })

        this.$toasted.success(this.$t('organisationDashboard.saveConfiguration.success') as string)
        this.$emit('saved')
        this.showModal = false

        console.info('Dashboard configuration saved successfully:', dashboardConfiguration)
      } catch (error: any) {
        console.error('Failed to save dashboard configuration:', error)
        this.$toasted.error(this.$t('organisationDashboard.saveConfiguration.error') as string)
      } finally {
        this.saving = false
      }
    },
    showResetConfirmation (): void {
      this.resetConfirmationVisible = true
    },
    async confirmResetToDefault (): Promise<void> {
      this.saving = true

      try {
        await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'saveDashboardConfiguration',
            dashboardConfiguration: DEFAULT_DASHBOARD_CONFIGURATION
          }
        })

        this.$toasted.success(this.$t('organisationDashboard.saveConfiguration.success') as string)
        this.resetConfirmationVisible = false
        this.$emit('reset')
        this.showModal = false

        console.info('Dashboard configuration reset to default and saved successfully:', DEFAULT_DASHBOARD_CONFIGURATION)
      } catch (error: any) {
        console.error('Failed to save default dashboard configuration:', error)
        this.$toasted.error(this.$t('organisationDashboard.saveConfiguration.error') as string)
      } finally {
        this.saving = false
      }
    }
  }
})
</script>
